import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  LogOut, Edit, Eye, Menu, X, Home, Users, 
  Building2, Bell, Search, Briefcase, UserPlus,
  GraduationCap, FolderKanban, MessageSquare, Send, Share2,
  Plus, Megaphone, Calendar, Video, Trash2, Copy
} from "lucide-react";
import { handleShare as safeHandleShare, getProfileShareUrlAsync, safeCopyToClipboard } from "@/utils/shareHelper";
import WhatsAppSupport from "@/components/WhatsAppSupport";
import ProfileCompleteness from "@/components/ProfileCompleteness";
import EventsSection from "@/components/EventsSection";
import CreateEventModal from "@/components/CreateEventModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

export default function EnterpriseDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [myOpportunities, setMyOpportunities] = useState([]);
  const [myAnnouncements, setMyAnnouncements] = useState([]);
  const [showOpportunityForm, setShowOpportunityForm] = useState(false);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [showInterviewCallModal, setShowInterviewCallModal] = useState(false);
  const [opportunityForm, setOpportunityForm] = useState({ 
    title: "", description: "", opportunity_type: "casting", 
    compensation: "", location: "", roles_needed: [] 
  });
  const [announcementForm, setAnnouncementForm] = useState({ title: "", content: "", announcement_type: "general" });
  const [shareModal, setShareModal] = useState({ open: false, url: "" });

  /**
   * Share handler - MUST be called directly in onClick (user gesture)
   * Opens native share sheet on mobile (WhatsApp, etc.)
   * Falls back to copy link if share unavailable
   */
  const handleShare = async () => {
    const shareUrl = await getProfileShareUrlAsync(profile, "enterprise");
    await safeHandleShare({
      url: shareUrl,
      title: `${profile?.personal_info?.full_name || "Company"} on TalentMeet`,
      text: `Check out ${profile?.personal_info?.full_name || "this company"}'s profile on TalentMeet!`,
      onShowModal: (url) => setShareModal({ open: true, url })
    });
  };

  // Copy from modal fallback
  const copyShareUrl = async () => {
    const success = await safeCopyToClipboard(shareModal.url);
    if (success) {
      toast.success("Link copied!");
      setShareModal({ open: false, url: "" });
    } else {
      toast.info("Long press the link to copy");
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      // Verify role is enterprise
      if (parsed.role !== "enterprise") {
        toast.error("Access denied. Redirecting to your dashboard.");
        window.location.href = `/dashboard/${parsed.role}`;
        return;
      }
      setUser(parsed);
    }
    fetchDashboardData();
    fetchMyOpportunities();
    fetchMyAnnouncements();
  }, []); // Remove navigate from dependencies

  const fetchMyOpportunities = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/opportunities/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyOpportunities(data.opportunities || []);
      }
    } catch (error) {
      console.error("Error fetching opportunities:", error);
    }
  };

  const fetchMyAnnouncements = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/announcements/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyAnnouncements(data.announcements || []);
      }
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
  };

  const createOpportunity = async () => {
    if (!opportunityForm.title || !opportunityForm.description) {
      toast.error("Please fill in title and description");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/opportunities`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(opportunityForm)
      });
      if (response.ok) {
        toast.success("Opportunity created!");
        setShowOpportunityForm(false);
        setOpportunityForm({ title: "", description: "", opportunity_type: "casting", compensation: "", location: "", roles_needed: [] });
        fetchMyOpportunities();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to create opportunity");
      }
    } catch (error) {
      toast.error("Failed to create opportunity");
    }
  };

  const createAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.content) {
      toast.error("Please fill in title and content");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/announcements`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(announcementForm)
      });
      if (response.ok) {
        toast.success("Announcement created!");
        setShowAnnouncementForm(false);
        setAnnouncementForm({ title: "", content: "", announcement_type: "general" });
        fetchMyAnnouncements();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to create announcement");
      }
    } catch (error) {
      toast.error("Failed to create announcement");
    }
  };

  const deleteOpportunity = async (opportunityId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/opportunities/${opportunityId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Opportunity deleted");
        fetchMyOpportunities();
      }
    } catch (error) {
      toast.error("Failed to delete opportunity");
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/announcements/${announcementId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Announcement deleted");
        fetchMyAnnouncements();
      }
    } catch (error) {
      toast.error("Failed to delete announcement");
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/dashboard/enterprise/data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.status === 403) {
        const err = await response.json();
        toast.error(err.detail || "Access denied");
        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        window.location.href = `/dashboard/${storedUser.role || "talent"}`;
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
        setProfile(data.profile);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
    toast.success("Logged out successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const managedTalents = dashboardData?.managed_talents || [];
  const linkedTrainers = dashboardData?.linked_trainers || [];
  const outgoingRequests = dashboardData?.outgoing_requests || [];
  const enterpriseInfo = dashboardData?.enterprise_info || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg object-cover" />
              <span className="font-display text-lg font-bold text-gray-900 hidden sm:block">TalentMeet</span>
              <Badge className="bg-blue-100 text-blue-700 ml-2">Enterprise</Badge>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              <Link to="/dashboard/enterprise" className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg">
                Dashboard
              </Link>
              <Link to="/browse" className="px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg">
                Talent Directory
              </Link>
              <Link to="/chat" className="px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg relative">
                Messages
              </Link>
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="w-5 h-5 text-gray-500" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn" className="text-gray-500">
                <LogOut className="w-5 h-5" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 p-3">
            <nav className="space-y-1">
              <Link to="/dashboard/enterprise" className="flex items-center gap-3 px-3 py-2 rounded-lg bg-blue-50 text-blue-600">
                <Home className="w-5 h-5" /> Dashboard
              </Link>
              <Link to="/enterprise/projects" className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
                <Briefcase className="w-5 h-5" /> Projects
              </Link>
              <Link to="/enterprise/talentboard" className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
                <FolderKanban className="w-5 h-5" /> TalentBoard
              </Link>
              <Link to="/browse" className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
                <Users className="w-5 h-5" /> Talent Directory
              </Link>
              <Link to="/chat" className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
                <MessageSquare className="w-5 h-5" /> Messages
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-5 mb-6 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold mb-1">
                {profile?.personal_info?.company_name || profile?.personal_info?.full_name || user?.name}
              </h1>
              <p className="text-white/80 text-sm">
                Manage your talent roster and discover new opportunities
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              <span className="font-medium">Enterprise Dashboard</span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-blue-50 border-blue-100">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-700">{stats.managed_talent_count || 0}</p>
              <p className="text-xs text-gray-600">Managed Talents</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-100">
            <CardContent className="p-4 text-center">
              <GraduationCap className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-orange-700">{stats.linked_trainer_count || 0}</p>
              <p className="text-xs text-gray-600">Linked Trainers</p>
            </CardContent>
          </Card>
          <Card className="bg-violet-50 border-violet-100">
            <CardContent className="p-4 text-center">
              <Send className="w-8 h-8 text-violet-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-violet-700">{stats.pending_requests || 0}</p>
              <p className="text-xs text-gray-600">Pending Requests</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-100">
            <CardContent className="p-4 text-center">
              <FolderKanban className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-700">0</p>
              <p className="text-xs text-gray-600">Active Projects</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Profile Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Company Profile</CardTitle>
                  {profile?.is_draft && <Badge variant="outline" className="text-xs">Draft</Badge>}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-4">
                  {profile?.portfolio?.logo || profile?.portfolio?.profile_photo ? (
                    <img 
                      src={profile.portfolio.logo || profile.portfolio.profile_photo} 
                      alt="Company Logo"
                      className="w-20 h-20 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-400 flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-white/80" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900">
                      {profile?.personal_info?.company_name || profile?.personal_info?.full_name || "Company Name"}
                    </h3>
                    <p className="text-sm text-gray-500 mb-2">
                      {enterpriseInfo?.company_type || profile?.enterprise_info?.company_type || "Enterprise"}
                    </p>
                    
                    {/* Profile Completion */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-500">Profile Completion</span>
                        <span className="font-medium text-blue-600">{profile?.completion_percentage || 0}%</span>
                      </div>
                      <Progress value={profile?.completion_percentage || 0} className="h-1.5" />
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => navigate("/enterprise/wizard")}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="edit-profile-btn"
                      >
                        <Edit className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => navigate(`/profile/${profile?.profile_id}`)}
                        data-testid="view-profile-btn"
                      >
                        <Eye className="w-3 h-3 mr-1" /> View
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={handleShare}
                        data-testid="share-profile-btn"
                        title="Copy shareable profile link"
                      >
                        <Share2 className="w-3 h-3 mr-1" /> Share
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-300"
                    onClick={() => setShowOpportunityForm(true)}
                    data-testid="create-opportunity-btn"
                  >
                    <Briefcase className="w-6 h-6 text-blue-600" />
                    <span className="text-xs font-medium">Create Casting</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-green-50 hover:border-green-300"
                    onClick={() => setShowAnnouncementForm(true)}
                    data-testid="create-announcement-btn"
                  >
                    <Megaphone className="w-6 h-6 text-green-600" />
                    <span className="text-xs font-medium">Announcement</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-purple-50 hover:border-purple-300"
                    onClick={() => setShowInterviewCallModal(true)}
                    data-testid="interview-call-btn"
                  >
                    <Video className="w-6 h-6 text-purple-600" />
                    <span className="text-xs font-medium">Interview Call</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* My Opportunities */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-blue-600" />
                    My Opportunities
                    {myOpportunities.length > 0 && <Badge variant="secondary">{myOpportunities.length}</Badge>}
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={() => setShowOpportunityForm(true)}>
                    <Plus className="w-4 h-4 mr-1" /> New
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {myOpportunities.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {myOpportunities.map(opp => (
                      <div key={opp.opportunity_id} className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900">{opp.title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">{opp.opportunity_type}</Badge>
                              <Badge variant={opp.status === "open" ? "default" : "secondary"} className="text-xs">
                                {opp.status}
                              </Badge>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {opp.applicants?.length || 0} applicants
                            </p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            onClick={() => deleteOpportunity(opp.opportunity_id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No opportunities posted yet</p>
                    <Button 
                      size="sm" 
                      className="mt-3 bg-blue-600 hover:bg-blue-700"
                      onClick={() => setShowOpportunityForm(true)}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Create First Opportunity
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Managed Talents */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Managed Talents
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={() => navigate("/browse")}>
                    <UserPlus className="w-4 h-4 mr-1" /> Find Talent
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {managedTalents.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {managedTalents.slice(0, 6).map(link => (
                      <div key={link.link_id} className="p-3 bg-gray-50 rounded-lg text-center">
                        <div className="w-12 h-12 rounded-full bg-violet-100 mx-auto mb-2 flex items-center justify-center">
                          <Users className="w-6 h-6 text-violet-600" />
                        </div>
                        <p className="text-sm font-medium text-gray-900 truncate">{link.target_name}</p>
                        <p className="text-xs text-gray-500 capitalize">{link.relationship_type?.replace(/_/g, " ")}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No managed talents yet</p>
                    <Button 
                      size="sm" 
                      className="mt-3 bg-blue-600 hover:bg-blue-700"
                      onClick={() => navigate("/browse")}
                    >
                      <Search className="w-4 h-4 mr-1" /> Browse Talent Directory
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Profile Completeness */}
            <ProfileCompleteness profile={profile} />

            {/* Events Section */}
            <EventsSection userRole="enterprise" title="Events" />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Projects Quick Access */}
            <Card className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white border-0">
              <CardContent className="p-4">
                <Briefcase className="w-8 h-8 mb-2" />
                <h3 className="font-semibold mb-1">Projects</h3>
                <p className="text-sm text-white/80 mb-3">Organize candidates by requirements & share with clients</p>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="w-full bg-white text-blue-600 hover:bg-gray-100"
                  onClick={() => navigate("/enterprise/projects")}
                  data-testid="projects-quick-access"
                >
                  View Projects
                </Button>
              </CardContent>
            </Card>

            {/* TalentBoard Quick Access */}
            <Card className="bg-gradient-to-br from-green-500 to-teal-500 text-white border-0">
              <CardContent className="p-4">
                <FolderKanban className="w-8 h-8 mb-2" />
                <h3 className="font-semibold mb-1">TalentBoard</h3>
                <p className="text-sm text-white/80 mb-3">Manage your shortlisted profiles</p>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="w-full bg-white text-green-600 hover:bg-gray-100"
                  onClick={() => navigate("/enterprise/talentboard")}
                >
                  Open TalentBoard
                </Button>
              </CardContent>
            </Card>

            {/* Talent Directory Quick Access */}
            <Card className="bg-gradient-to-br from-violet-500 to-pink-500 text-white border-0">
              <CardContent className="p-4">
                <Search className="w-8 h-8 mb-2" />
                <h3 className="font-semibold mb-1">Talent Directory</h3>
                <p className="text-sm text-white/80 mb-3">Discover and connect with top talents</p>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="w-full bg-white text-violet-600 hover:bg-gray-100"
                  onClick={() => navigate("/browse")}
                >
                  Browse Talents
                </Button>
              </CardContent>
            </Card>

            {/* Trainer Directory */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-orange-600" />
                  Linked Trainers
                </CardTitle>
              </CardHeader>
              <CardContent>
                {linkedTrainers.length > 0 ? (
                  <div className="space-y-2">
                    {linkedTrainers.map(link => (
                      <div key={link.link_id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                          <GraduationCap className="w-4 h-4 text-orange-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{link.target_name}</p>
                          <p className="text-xs text-gray-500">Trainer</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <p className="text-sm">No linked trainers</p>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="mt-2"
                      onClick={() => navigate("/browse")}
                    >
                      Find Trainers
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Outgoing Requests */}
            {outgoingRequests.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Send className="w-5 h-5 text-violet-600" />
                    Pending Requests
                    <Badge className="ml-auto">{outgoingRequests.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {outgoingRequests.slice(0, 5).map(req => (
                    <div key={req.link_id} className="flex items-center gap-2 p-2 bg-violet-50 rounded-lg">
                      <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center">
                        <Send className="w-4 h-4 text-violet-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{req.target_name}</p>
                        <p className="text-xs text-gray-500 capitalize">{req.target_profile_type}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">Pending</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Managed Profiles Placeholder */}
            <Card className="border-dashed border-2 border-gray-200">
              <CardContent className="p-4 text-center">
                <Briefcase className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <h3 className="font-medium text-gray-700 text-sm mb-1">Managed Profiles</h3>
                <p className="text-xs text-gray-500 mb-2">
                  Full profile management features
                </p>
                <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
              </CardContent>
            </Card>

            {/* My Announcements */}
            {myAnnouncements.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-green-600" />
                    My Announcements
                    <Badge variant="secondary" className="ml-auto">{myAnnouncements.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-48 overflow-y-auto">
                  {myAnnouncements.slice(0, 5).map(ann => (
                    <div key={ann.announcement_id} className="p-2 bg-gray-50 rounded-lg border flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-xs text-gray-900 truncate">{ann.title}</p>
                        <p className="text-xs text-gray-400">{new Date(ann.created_at).toLocaleDateString()}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-5 w-5 p-0 text-red-500"
                        onClick={() => deleteAnnouncement(ann.announcement_id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Create Opportunity Modal */}
      {showOpportunityForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-blue-600" />
                Create Opportunity
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input 
                  placeholder="e.g., Lead Actor for Web Series"
                  value={opportunityForm.title}
                  onChange={(e) => setOpportunityForm({...opportunityForm, title: e.target.value})}
                  data-testid="opportunity-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea 
                  placeholder="Describe the role, project, and requirements..."
                  rows={4}
                  value={opportunityForm.description}
                  onChange={(e) => setOpportunityForm({...opportunityForm, description: e.target.value})}
                  data-testid="opportunity-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <select 
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={opportunityForm.opportunity_type}
                    onChange={(e) => setOpportunityForm({...opportunityForm, opportunity_type: e.target.value})}
                  >
                    <option value="casting">Casting</option>
                    <option value="audition">Audition</option>
                    <option value="project">Project</option>
                    <option value="collaboration">Collaboration</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input 
                    placeholder="e.g., Mumbai"
                    value={opportunityForm.location}
                    onChange={(e) => setOpportunityForm({...opportunityForm, location: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Compensation</Label>
                <Input 
                  placeholder="e.g., ₹50,000 - ₹1,00,000 per day"
                  value={opportunityForm.compensation}
                  onChange={(e) => setOpportunityForm({...opportunityForm, compensation: e.target.value})}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowOpportunityForm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={createOpportunity}
                  data-testid="submit-opportunity-btn"
                >
                  Publish
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Announcement Modal */}
      {showAnnouncementForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-green-600" />
                Create Announcement
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input 
                  placeholder="Announcement title"
                  value={announcementForm.title}
                  onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Content</Label>
                <Textarea 
                  placeholder="Write your announcement..."
                  rows={4}
                  value={announcementForm.content}
                  onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <select 
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={announcementForm.announcement_type}
                  onChange={(e) => setAnnouncementForm({...announcementForm, announcement_type: e.target.value})}
                >
                  <option value="general">General</option>
                  <option value="hiring">Hiring</option>
                  <option value="event">Event</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowAnnouncementForm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={createAnnouncement}
                >
                  Publish
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Share Link Modal - Fallback when native share unavailable */}
      <Dialog open={shareModal.open} onOpenChange={(open) => setShareModal({ ...shareModal, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-600" />
              Share Company Profile
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Copy this link to share on WhatsApp, Instagram, or anywhere:
            </p>
            <div className="flex items-center gap-2">
              <Input 
                value={shareModal.url} 
                readOnly 
                className="text-sm font-mono bg-gray-50"
                onClick={(e) => e.target.select()}
                onFocus={(e) => e.target.select()}
              />
              <Button onClick={copyShareUrl} size="sm" className="shrink-0">
                <Copy className="w-4 h-4 mr-1" /> Copy
              </Button>
            </div>
            <p className="text-xs text-gray-500 text-center">
              Tap the link to select, then tap Copy button
            </p>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Interview Call Modal - Pre-filled Create Event */}
      <CreateEventModal
        isOpen={showInterviewCallModal}
        onClose={() => setShowInterviewCallModal(false)}
        onSuccess={() => {
          setShowInterviewCallModal(false);
          // Refresh events in EventsSection would happen via that component's own state
        }}
        userRole="enterprise"
        initialValues={{
          event_type: "meeting",
          visibility_scope: "private"
        }}
      />
      
      <WhatsAppSupport />
    </div>
  );
}
